import React from "react";
import { Badge, Button, Card, Col, Form, Pagination, Row } from "react-bootstrap";
import DrugCard from "../components/card/DrugCard";
import CartModal from "../components/modals/CardModal";
import Spinner1 from "../components/common/spinners";
import DrugCartModal from "../components/modals/drugSideCart";
import useProfileStore from "../store/patient/profileStore";

const DrugMart = ({ drugMart, loading, handleQueryMart }) => {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin] = React.useState(currentSkin);
  const [show, setShow] = React.useState(false);
  const [cartItems, setCartItems] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const { profile } = useProfileStore();

  const openModal = () => {
    setShow(true);
  };

  const addToCart = (drug) => {
    setCartItems((prev) => {
      const existingItem = prev.find((item) => item.id === drug.id);

      if (existingItem) {
        return prev.filter((item) => item.id !== drug.id);
      }

      return [...prev, { ...drug, requestQuantity: 1 }];
    });
  };

  const updateQuantity = (id, newQuantity) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, requestQuantity: newQuantity } : item
      )
    );
  };

  const switchSkin = (mode) => {
    if (mode === "dark") {
      const btnWhite = document.getElementsByClassName("btn-white");

      for (const btn of btnWhite) {
        btn.classList.add("btn-outline-primary");
        btn.classList.remove("btn-white");
      }
      return;
    }

    const btnOutlinePrimary = document.getElementsByClassName("btn-outline-primary");

    for (const btn of btnOutlinePrimary) {
      btn.classList.remove("btn-outline-primary");
      btn.classList.add("btn-white");
    }
  };

  switchSkin(skin);
  React.useEffect(() => {
    switchSkin(skin);
  }, [skin]);

  const otcDrugs = Array.isArray(drugMart?.result)
    ? drugMart.result.filter((item) => item?.generic?.overTheCounter === true)
    : [];
  const totalEstimate = cartItems.reduce((sum, item) => {
    const cost = item?.batches?.[0]?.costPrice || 0;
    const quantity = item?.requestQuantity || 1;

    return sum + cost * quantity;
  }, 0);

  return (
    <React.Fragment>
      <section className="drugmart-hero">
        <div className="drugmart-hero-copy">
          <span className="drugmart-kicker">Pharmacy</span>
          <h2>Drug Mart</h2>
          <p>
            Browse over-the-counter medications in a cleaner, calmer storefront and
            build your order with less friction.
          </p>
        </div>

        <div className="drugmart-hero-stats">
          <div className="drugmart-stat">
            <small>Available Items</small>
            <strong>{otcDrugs.length}</strong>
          </div>
          <div className="drugmart-stat">
            <small>In Cart</small>
            <strong>{cartItems.length}</strong>
          </div>
          <div className="drugmart-stat">
            <small>Estimated Total</small>
            <strong>
              {totalEstimate.toLocaleString("en-NG", {
                style: "currency",
                currency: "NGN",
              })}
            </strong>
          </div>
        </div>
      </section>

      <Row className="g-4 align-items-start">
        <Col xl={9}>
          <Card className="card-event drugmart-shell">
            <Card.Body className="p-3 p-lg-4">
              <div className="drugmart-toolbar">
                <div className="drugmart-toolbar-copy">
                  <div className="divider divider-start fw-medium mb-2">
                    <span>Medication Store</span>
                  </div>
                  <h5 className="mb-1">Find everyday medications quickly</h5>
                  <p className="mb-0">
                    Search by drug name, compare availability, and add the items you
                    need to your request cart.
                  </p>
                </div>

                <div className="drugmart-toolbar-actions">
                  <div className="drugmart-search">
                    <Form.Control
                      type="text"
                      placeholder="Search for a drug"
                      value={searchTerm}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSearchTerm(value);
                        handleQueryMart(value, profile?.hmo?.id);
                      }}
                    />
                  </div>

                  <Button
                    onClick={openModal}
                    variant=""
                    className="btn-white drugmart-cart-btn"
                  >
                    <i className="ri-shopping-cart-line"></i>
                    <span>Open Cart</span>
                    {cartItems.length > 0 && (
                      <Badge bg="success" pill>
                        {cartItems.length}
                      </Badge>
                    )}
                  </Button>
                </div>
              </div>

              <div className="drugmart-results-meta">
                <span>{otcDrugs.length} medications available</span>
                <span>Showing over-the-counter options only</span>
              </div>

              <Row sm={1} md={2} xl={3} lg={3} className="g-3">
                {loading ? (
                  <div className="w-100 d-flex justify-content-center py-5">
                    <Spinner1 />
                  </div>
                ) : otcDrugs.length > 0 ? (
                  otcDrugs.map((drug) => {
                    const cartedItem = cartItems.find((item) => item.id === drug.id);

                    return (
                      <DrugCard
                        key={drug.id}
                        drug={drug}
                        carted={!!cartedItem}
                        addToCart={addToCart}
                        updateQuantity={updateQuantity}
                        quantity={cartedItem ? cartedItem.requestQuantity : 1}
                      />
                    );
                  })
                ) : (
                  <div className="drugmart-empty">
                    <i className="ri-capsule-line"></i>
                    <h6>No medications found</h6>
                    <p>Try another drug name or clear the search to see more options.</p>
                  </div>
                )}
              </Row>

              <div className="w-100 d-flex justify-content-end mt-4">
                <Pagination className="drugmart-pagination mb-0">
                  <Pagination.First />
                  <Pagination.Prev />
                  <Pagination.Next />
                  <Pagination.Last />
                </Pagination>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} className="d-none d-md-block">
          <div className="drugmart-cart-panel">
            <div className="drugmart-cart-panel-head">
              <small>Order Cart</small>
              <strong>{cartItems.length} item(s)</strong>
            </div>
            <DrugCartModal
              setCartItems={setCartItems}
              setShow={setShow}
              show={true}
              cartItems={cartItems}
              drugMart={drugMart}
            />
          </div>
        </Col>
      </Row>

      <CartModal
        setCartItems={setCartItems}
        setShow={setShow}
        show={show}
        cartItems={cartItems}
        drugMart={drugMart}
      />
    </React.Fragment>
  );
};

export default DrugMart;
